/**
 * Client d'authentification — le SEUL endroit qui parle au backend d'auth.
 *
 * Le jeton CSRF est récupéré via GET /api/v1/auth/session puis envoyé en
 * en-tête `x-csrf-token` sur chaque mutation. Login et MFA n'ont pas encore de
 * session, donc pas de CSRF (voir le backend : SameSite=Lax les couvre).
 */

let csrfToken: string | null = null

async function refreshCsrf(): Promise<void> {
  const r = await fetch('/api/v1/auth/session', { credentials: 'same-origin' })
  if (r.ok) {
    const j = (await r.json()) as { csrf_token: string | null }
    csrfToken = j.csrf_token
  }
}

async function post<T>(path: string, body?: unknown): Promise<{ status: number; data: T }> {
  // Chaque chargement de page repart de zéro : le jeton CSRF se récupère
  // paresseusement avant la première mutation.
  if (!csrfToken) await refreshCsrf()
  const headers: Record<string, string> = {}
  if (csrfToken) headers['x-csrf-token'] = csrfToken
  const init: RequestInit = { method: 'POST', credentials: 'same-origin', headers }
  if (body !== undefined) {
    // content-type UNIQUEMENT quand il y a un corps : Fastify refuse un JSON
    // annoncé mais vide.
    headers['content-type'] = 'application/json'
    init.body = JSON.stringify(body)
  }
  const r = await fetch(path, init)
  const data = (await r.json().catch(() => ({}))) as T
  return { status: r.status, data }
}

export interface SessionState {
  authenticated: boolean
  state: string | null
  csrf_token: string | null
  enforcement: boolean
}

export async function getSession(): Promise<SessionState> {
  const r = await fetch('/api/v1/auth/session', { credentials: 'same-origin' })
  const j = (await r.json()) as SessionState
  csrfToken = j.csrf_token
  return j
}

export interface Me {
  user: { id: string; display_name: string; email: string } | null
  roles: string[]
  permissions: string[]
  mfa_enabled: boolean
  _actions: string[]
}
export async function getMe(): Promise<Me> {
  const r = await fetch('/api/v1/me', { credentials: 'same-origin' })
  return (await r.json()) as Me
}

type LoginResult = { ok: boolean; state?: string; error?: string; message?: string }

export async function login(email: string, password: string, remember = false): Promise<{ status: number } & LoginResult> {
  const { status, data } = await post<LoginResult>('/api/v1/auth/login', { email, password, remember })
  await refreshCsrf()
  return { status, ...data }
}

export async function submitMfa(code: string, mode: 'totp' | 'recovery'): Promise<{ status: number } & LoginResult> {
  const { status, data } = await post<LoginResult>('/api/v1/auth/mfa', { code, mode })
  await refreshCsrf()
  return { status, ...data }
}

export async function logout(): Promise<void> {
  await post('/api/v1/auth/logout')
  csrfToken = null
}

export async function enrollMfa(): Promise<{ qr_data_url: string; secret: string }> {
  const { data } = await post<{ qr_data_url: string; secret: string }>('/api/v1/auth/mfa/enroll')
  return data
}

export async function confirmMfa(code: string): Promise<{ ok: boolean; recovery_codes: string[] }> {
  const { data } = await post<{ ok: boolean; recovery_codes: string[] }>('/api/v1/auth/mfa/confirm', { code })
  return data
}

/* ─── WebAuthn (LOT 5.5.1) ─── */

export interface MfaMethods { totp: boolean; passkey: boolean; preferred: string | null }

export async function getMfaMethods(): Promise<MfaMethods | null> {
  const r = await fetch('/api/v1/auth/mfa/methods', { credentials: 'same-origin' })
  if (!r.ok) return null
  return (await r.json()) as MfaMethods
}

export async function passkeyMfaOptions(): Promise<Record<string, unknown> | null> {
  const { status, data } = await post<{ options?: Record<string, unknown> }>('/api/v1/auth/webauthn/mfa/options')
  return status === 200 ? (data.options ?? null) : null
}

export async function passkeyMfaVerify(response: unknown): Promise<{ status: number } & LoginResult> {
  const { status, data } = await post<LoginResult>('/api/v1/auth/webauthn/mfa/verify', { response })
  await refreshCsrf()
  return { status, ...data }
}
